import type { Map as MlMap, MapMouseEvent, PointLike } from 'maplibre-gl';
import { totemLayerIds } from './totems-layer';
import type {
  CatalogItem,
  KingdomProperties,
  PlotProperties,
  StructureProperties,
} from '../api/models';
import type { AccountState } from '../hud/account-state';

// In-map details panel for the realm map, mirroring the mobile tap sheets: a
// built structure shows its type, builder, and level; anywhere else inside a
// kingdom shows the kingdom's stats. Replaces the old MapLibre popups (whose
// default white bubble rendered our dark-scheme white text invisibly) with a
// framed panel inside the map window that has room to grow player controls.
//
// Content builders and the actionsFor permission gate are pure (and escape
// all server data — names/usernames are player-authored text going into
// innerHTML) so they unit-test directly; createDetailsPanel is the thin DOM
// shell around them.

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export type Selection =
  | { kind: 'structure'; props: StructureProperties }
  | { kind: 'kingdom'; props: KingdomProperties }
  // An unclaimed plot clicked in build mode; gold is the balance snapshot the
  // build menu prices against, error a failed build's server message.
  | {
      kind: 'plot';
      props: PlotProperties & { building_id: string };
      gold: number;
      error?: string;
    };

// The details panel's window into build mode (created by build-mode.ts):
// undefined until the signed-in player's kingdom is known.
export interface BuildContext {
  isMine(kingdomId: string): boolean;
  active: boolean;
  catalog: CatalogItem[];
  toggle(): void;
  build(buildingId: string, type: string): void;
}

export function structureDetailsHtml(p: StructureProperties): string {
  const builder = p.builder_username
    ? `Built by ${escapeHtml(p.builder_username)}`
    : 'Built by a departed noble';
  return (
    `<header>` +
    `<h2>${escapeHtml(p.name)}</h2>` +
    `<span class="details-chip">Level ${Number(p.level) || 1}</span>` +
    `</header>` +
    `<p>${builder}</p>`
  );
}

export function kingdomDetailsHtml(p: KingdomProperties): string {
  const nobles = Number(p.noble_count) || 0;
  const chip = p.rank_tier
    ? `<span class="details-chip">${escapeHtml(p.rank_tier)}</span>`
    : '';
  const rank = p.rank_tier ? ` · Rank ${Number(p.rank_score) || 0}` : '';
  return (
    `<header>` +
    `<h2>${escapeHtml(p.name)}</h2>` +
    chip +
    `</header>` +
    `<p>Ruled by ${escapeHtml(p.ruler_name)}</p>` +
    `<p>${nobles} noble${nobles === 1 ? '' : 's'}${rank}</p>`
  );
}

// A control the signed-in player may use on the current selection, rendered
// as a button in the panel's actions row. Disabled buttons render but don't
// fire (unaffordable build-menu rows, mobile parity).
export interface PanelAction {
  id: string;
  label: string;
  run: () => void;
  disabled?: boolean;
}

// actionsFor is the permission gate between the account state and the panel's
// controls. The web client is low-trust (blueprint §4): building is asset
// management and rides here through the build context; conquest/combat
// actions stay mobile-only.
export function actionsFor(
  account: AccountState,
  selection: Selection,
  build?: BuildContext,
): PanelAction[] {
  if (account.kind !== 'signed_in') return [];
  switch (selection.kind) {
    case 'structure':
      return []; // mobile parity: the structure sheet has no controls either
    case 'kingdom':
      // Build mode is the panel's first action — own kingdom only. Future:
      // mirror mobile's propose/vote controls.
      if (build?.isMine(selection.props.id)) {
        return [
          {
            id: 'build-mode',
            label: build.active ? 'Exit build mode' : 'Enter build mode',
            run: () => build.toggle(),
          },
        ];
      }
      return [];
    case 'plot': {
      if (!build?.active) return [];
      const buildingId = selection.props.building_id;
      return build.catalog.map((item) => ({
        id: `build-${item.type}`,
        label: `${item.name} — ${item.cost} gold`,
        disabled: item.cost > selection.gold,
        run: () => build.build(buildingId, item.type),
      }));
    }
  }
}

// The build menu body: an unclaimed plot with the player's balance (and a
// failed build's message). The catalog itself renders as the actions row.
export function plotDetailsHtml(gold: number, error?: string): string {
  const warning = error
    ? `<p class="details-error">${escapeHtml(error)}</p>`
    : '';
  return (
    `<header><h2>Unclaimed plot</h2>` +
    `<span class="details-chip">${gold} gold</span></header>` +
    `<p>Raise a structure for your kingdom</p>` +
    warning
  );
}

// selectionHtml is the shared body markup for a selection: the details panel
// wraps it with chrome and actions; the hover tooltip (hover.ts) shows it
// bare (plots never reach hover — it only queries structure/kingdom layers).
export function selectionHtml(selection: Selection): string {
  switch (selection.kind) {
    case 'structure':
      return structureDetailsHtml(selection.props);
    case 'kingdom':
      return kingdomDetailsHtml(selection.props);
    case 'plot':
      return plotDetailsHtml(selection.gold, selection.error);
  }
}

// detailsHtml assembles the full panel markup: close button, the selection's
// details, and an actions row only when there are actions.
export function detailsHtml(
  selection: Selection,
  actions: PanelAction[],
): string {
  const body = selectionHtml(selection);
  const buttons = actions
    .map(
      (a) =>
        `<button type="button" data-action="${escapeHtml(a.id)}"` +
        `${a.disabled ? ' disabled' : ''}>` +
        `${escapeHtml(a.label)}</button>`,
    )
    .join('');
  return (
    `<button class="details-close" type="button" aria-label="Close">✕</button>` +
    body +
    (buttons ? `<div class="details-actions">${buttons}</div>` : '')
  );
}

export interface DetailsPanel {
  show(selection: Selection): void;
  close(): void;
  // Re-render the open selection — call when the account state changes so the
  // controls follow the viewer's permissions without losing the selection.
  refresh(): void;
  wire(map: MlMap): void;
}

export function createDetailsPanel(
  el: HTMLElement,
  getAccount: () => AccountState,
  // Both optional and lazily evaluated: build mode is created after the map
  // (and only matters signed-in), so the panel asks each render/click.
  opts: {
    getBuildContext?: () => BuildContext | undefined;
    resolvePlot?: (point: PointLike) => Selection | null;
  } = {},
): DetailsPanel {
  let selection: Selection | null = null;

  function render(): void {
    if (!selection) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }
    const actions = actionsFor(
      getAccount(),
      selection,
      opts.getBuildContext?.(),
    );
    el.innerHTML = detailsHtml(selection, actions);
    el.hidden = false;
    el.querySelector('.details-close')?.addEventListener('click', close);
    for (const action of actions) {
      if (action.disabled) continue;
      el.querySelector(`[data-action="${CSS.escape(action.id)}"]`)
        ?.addEventListener('click', () => action.run());
    }
  }

  function show(next: Selection): void {
    selection = next;
    render();
  }

  function close(): void {
    selection = null;
    render();
  }

  return {
    show,
    close,
    refresh: render,
    // wire attaches the single map click handler. Structures win over the
    // kingdom containing them (small target under a big polygon), matching
    // the mobile tap precedence; clicking open ground dismisses the panel,
    // like tapping away from a mobile sheet.
    wire(map: MlMap): void {
      map.on('click', (e: MapMouseEvent) => {
        // Build mode's unbuilt plots win while active — they're the small
        // targets the mode exists to expose (mobile tap precedence).
        const plot = opts.resolvePlot?.(e.point);
        if (plot) {
          show(plot);
          return;
        }

        const structureLayers = [...totemLayerIds(), 'structures-fill'].filter(
          (id) => map.getLayer(id) !== undefined,
        );
        const structure = map.queryRenderedFeatures(e.point, {
          layers: structureLayers,
        })[0];
        if (structure) {
          show({
            kind: 'structure',
            props: structure.properties as StructureProperties,
          });
          return;
        }

        const kingdom =
          map.getLayer('kingdoms-fill') !== undefined
            ? map.queryRenderedFeatures(e.point, {
                layers: ['kingdoms-fill'],
              })[0]
            : undefined;
        if (kingdom) {
          show({
            kind: 'kingdom',
            props: kingdom.properties as KingdomProperties,
          });
          return;
        }

        close();
      });
    },
  };
}
